import type { ReactNode } from "react";
import { useCallback, useEffect, useState } from "react";
import { Badge, Button, Dropdown } from "antd";
import type { MenuProps } from "antd";
import {
  BellOutlined,
  MenuOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth, isRealUser } from "../../../auth/AuthContext";
import { useTheme } from "../../../theme/ThemeProvider";
import { computeInitials } from "../../common/Avatar";
import {
  getUnreadForumCounts,
  listTripsWithMeta,
  type TripUnreadCount,
} from "../../../api/trips";
import { buildLocalePath } from "../../../i18n/localePath";
import { LocaleLink } from "../../../i18n/LocaleLink";
import { ThemeMenuSection, PreferencesMenuSection, UserMenuTrigger, useDropdown } from "../shared";
import "./TopNav.less";

const UNREAD_POLL_MS = 5 * 60 * 1000;

function PageNav({ isAdmin, navigate, t, lang }: {
  isAdmin: boolean;
  navigate: (to: string) => void;
  t: (key: string) => string;
  lang: string;
}) {
  return (
    <nav aria-label="Page navigation" className="daynav-topbar__page-nav">
      <button type="button" data-testid="nav-home" className="daynav-topbar__link" onClick={() => navigate(buildLocalePath(lang, "/"))}>
        {t("common:nav.home")}
      </button>
      <button type="button" data-testid="nav-help" className="daynav-topbar__link" onClick={() => navigate(buildLocalePath(lang, "/help"))}>
        {t("common:nav.help")}
      </button>
      <button type="button" data-testid="nav-about" className="daynav-topbar__link" onClick={() => navigate(buildLocalePath(lang, "/about"))}>
        {t("common:nav.about")}
      </button>
      {isAdmin && (
        <button type="button" data-testid="nav-admin" className="daynav-topbar__link" onClick={() => navigate("/admin")}>
          {t("common:nav.admin")}
        </button>
      )}
    </nav>
  );
}

export function TopNav({
  showMenuButton = false,
  onMenuClick,
  rightExtra,
  overlay = false,
}: {
  showMenuButton?: boolean;
  onMenuClick?: () => void;
  rightExtra?: ReactNode;
  overlay?: boolean;
}) {
  const { user, signOut } = useAuth();
  const { effective } = useTheme();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [unreadCounts, setUnreadCounts] = useState<TripUnreadCount[]>([]);
  const [tripNames, setTripNames] = useState<Map<string, string>>(new Map());

  const refreshUnread = useCallback(async () => {
    if (!user) return;
    try {
      const counts = await getUnreadForumCounts();
      if (!Array.isArray(counts)) return;
      setUnreadCounts(counts);
      if (counts.length > 0) {
        const { trips } = await listTripsWithMeta({ includeArchived: false });
        const map = new Map<string, string>();
        for (const t of trips) map.set(t.id, t.title);
        setTripNames(map);
      }
    } catch {
      // Polling failure is non-fatal
    }
  }, [user]);

  useEffect(() => {
    refreshUnread();
    const id = setInterval(refreshUnread, UNREAD_POLL_MS);
    return () => clearInterval(id);
  }, [refreshUnread]);

  const userMenu = useDropdown();

  const totalUnread = unreadCounts.reduce((sum, c) => sum + c.count, 0);
  const unreadMenuItems: MenuProps["items"] = unreadCounts.map((uc) => ({
    key: uc.tripId,
    label: `${tripNames.get(uc.tripId) ?? "Trip"} — ${uc.count} new message${uc.count === 1 ? "" : "s"}`,
    onClick: () => navigate(`/trips/${uc.tripId}#collab`),
  }));

  const notificationBell =
    user && totalUnread > 0 ? (
      <Dropdown trigger={["click"]} menu={{ items: unreadMenuItems }}>
        <button
          type="button"
          data-testid="unread-forum-badge"
          aria-label={`${totalUnread} unread forum messages`}
          className="daynav-topbar__notif-btn"
        >
          <Badge count={totalUnread} size="small">
            <BellOutlined className="daynav-topbar__notif-icon" />
          </Badge>
        </button>
      </Dropdown>
    ) : null;

  return (
    <header
      role="banner"
      className={`daynav-topbar${overlay ? " daynav-topbar--overlay" : ""}`}
      data-testid="topbar"
    >
      <div className="daynav-topbar__left">
        {showMenuButton && (
          <Button
            type="text"
            icon={<MenuOutlined />}
            onClick={onMenuClick}
            aria-label={t("common:nav.openItinerary")}
            className="daynav-topbar__theme-btn"
          />
        )}
        <LocaleLink
          to="/"
          aria-label={t("common:nav.tripsDashboard")}
          className="daynav-topbar__brand"
        >
          <img
            src={overlay || effective === "dark" ? "/logo_wb_dark.png" : "/logo_wb.png"}
            alt=""
            width={100}
            height={26}
          />
        </LocaleLink>
        <PageNav isAdmin={!!user?.isAdmin} navigate={navigate} t={t} lang={i18n.language} />
      </div>
      <div className="daynav-topbar__actions">
        {rightExtra}
        {notificationBell}
        <div ref={userMenu.ref} className="topnav-dropdown-anchor">
          {user ? (
            <UserMenuTrigger
              image={user.image ?? null}
              initials={computeInitials(user.nickname ?? user.name ?? "", user.email)}
              onClick={() => userMenu.setOpen(!userMenu.open)}
            />
          ) : (
            <button
              type="button"
              data-testid="user-menu-trigger"
              aria-label="Open account menu"
              className="daynav-topbar__avatar"
              onClick={() => userMenu.setOpen(!userMenu.open)}
            >
              <span className="daynav-topbar__avatar-initials" aria-hidden="true">
                <UserOutlined />
              </span>
            </button>
          )}
          {userMenu.open && (
            <div className="topnav-dropdown topnav-user-dropdown" data-testid="user-dropdown">
              {isRealUser(user) ? (
                <>
                  <button
                    type="button"
                    data-testid="user-menu-settings"
                    className="topnav-menu-item"
                    onClick={() => { userMenu.setOpen(false); navigate("/settings"); }}
                  >
                    {t("common:nav.settings")}
                  </button>
                  <button
                    type="button"
                    data-testid="user-menu-logout"
                    className="topnav-menu-item"
                    onClick={() => { userMenu.setOpen(false); void signOut(); }}
                  >
                    {t("common:nav.logout")}
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    data-testid="user-menu-login"
                    className="topnav-menu-item"
                    onClick={() => { userMenu.setOpen(false); navigate("/login"); }}
                  >
                    {t("common:nav.login")}
                  </button>
                  <button
                    type="button"
                    data-testid="user-menu-register"
                    className="topnav-menu-item"
                    onClick={() => { userMenu.setOpen(false); navigate("/register"); }}
                  >
                    {t("common:nav.register")}
                  </button>
                </>
              )}
              <ThemeMenuSection onSelect={() => userMenu.setOpen(false)} />
              <PreferencesMenuSection onSelect={() => userMenu.setOpen(false)} />
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
